/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {forwardRef, LegacyRef, memo, useCallback, useRef, useState} from "react";
import {BiRightArrow} from "react-icons/bi";
import {useResizeDetector} from "react-resize-detector";
import {Typography} from '@mui/material';
import {ARGOS_ERROR, ARGOS_WARNING} from "../../../../constants/colors";

interface ICustomRef {
  [idx: number]: React.RefObject<HTMLDivElement>
}

type GetRefCallbackType = (idx: number) => (React.RefObject<HTMLDivElement>)
const useGetRef = (): GetRefCallbackType => {
  const refs: React.MutableRefObject<ICustomRef | undefined> = React.useRef<ICustomRef>({})
  return React.useCallback(
    (idx: number) => (refs.current!![idx] ??= React.createRef<HTMLDivElement>()),
    [refs]
  )
}

const getIsOverflowing = (containerRef: HTMLElement) => {
  return containerRef.offsetHeight < containerRef.scrollHeight || containerRef.offsetWidth < containerRef.scrollWidth;
}

type WrapStyleType = Pick<React.CSSProperties, 'whiteSpace' | 'textOverflow' | 'overflow'>
const getMargins = (wouldOverflow: boolean, isExpanded: boolean): WrapStyleType => {
  const shouldChangeMargins = wouldOverflow && isExpanded

  const wrapStyle: WrapStyleType = {
    whiteSpace: shouldChangeMargins ? 'normal' : 'nowrap',
    textOverflow: shouldChangeMargins ? 'fade' : 'ellipsis',
    overflow: shouldChangeMargins ? 'auto' : 'hidden'
  }

  return wrapStyle
}

interface IItemProps {
  line: IMessage
  wouldOverflow: boolean
  index?: number
  enumerate: boolean
  color?: string
}

const Item = memo(forwardRef((props: IItemProps, ref: LegacyRef<HTMLDivElement>) => {
    const {line, wouldOverflow, index, enumerate, color} = props
    const [isExpanded, setIsExpanded] = useState(false)
    const margin = {
      marginTop: 5,
    }
    const indexString = index == undefined? "": `${index + 1}. `
    const textColor = color
    const text = line?.text
    return (
      <div style={{borderBottom: `1px solid #D3D3D3`, ...margin}}>
        <div ref={ref} style={{...getMargins(wouldOverflow, isExpanded), padding: '0px 5px 0px 10px', whiteSpace: "pre-line"}}>
          {wouldOverflow &&
            (<BiRightArrow
              onClick={() => setIsExpanded(prevState => !prevState)}
              style={{
                transition: "all 0.25s ease",
                transform: `rotate(${!isExpanded ? 0 : "0.25turn"})`,
                marginRight: 3
              }}
            />)
          }
          <span style={{color: textColor}}>
            {enumerate && indexString} {text}
          </span>
        </div>
      </div>
    )
  })
);

export interface IMessage {
  text: string
  color: string
}

interface IProps {
  statementMessages: IMessage[] | undefined
  errorMessages: IMessage[]
  warningMessages: IMessage[] | undefined
  title: string
  enumerate: boolean
}

export const FileProcessingMessages = memo((props: IProps) => {
  const {statementMessages, errorMessages, warningMessages, title, enumerate} = props
  const widthOfOriginalTextList = useRef<number[]>([])
  const getRef: GetRefCallbackType = useGetRef()
  const [wouldOverflowList, setWouldOverflowList] = useState<(boolean)[]>(Array(errorMessages?.length).fill(false))

  const hasOverflownList = useRef<boolean[]>([])

  const onResize = useCallback((width?: number) => {
    if (width == null) return
    const arr = [...wouldOverflowList]
    let shouldUpdate = false
    for (let ind = 0; ind < errorMessages?.length; ++ind) {
      const tempRef = getRef(ind)
      if (tempRef.current == null) continue
      const isOverflowing = getIsOverflowing(tempRef.current)
      const hasOverflown = hasOverflownList.current[ind]

      if (!hasOverflown && !isOverflowing) continue
      const wouldOverflow = wouldOverflowList[ind]
      if (!hasOverflown && isOverflowing) {
        widthOfOriginalTextList.current[ind] = tempRef.current.scrollWidth
        hasOverflownList.current[ind] = true
        if (!wouldOverflow) {
          shouldUpdate = true
          arr[ind] = true
        }
        continue
      }
      if (hasOverflown) {
        if (width < widthOfOriginalTextList.current[ind]) {
          shouldUpdate = shouldUpdate || wouldOverflow !== true
          if (wouldOverflow !== true) arr[ind] = true
        } else {
          shouldUpdate = shouldUpdate || wouldOverflow !== false
          if (wouldOverflow !== false) arr[ind] = false
        }
      }
    }
    if (shouldUpdate) {
      setWouldOverflowList(arr)
    }
  }, [getRef, errorMessages, wouldOverflowList])
  const {ref} = useResizeDetector({onResize, handleWidth: true},);

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 200,
      maxHeight: 200,
      overflowY: 'auto',
    }}
    >
      <Typography variant='h5' component={'div'} marginBottom={1}>{title}</Typography>
      <div style={{
        height: '100%',
        width: '100%',
        border: "1px solid grey",
        borderRadius: 10,
        padding: 0,
        display: 'flex',
        flexFlow: 'column',
        overflowY: 'auto', 
      }} ref={ref}>
        {statementMessages?.map((line, index) => {
          const text = line?.text
          if (text == null || text.length === 0) return null
          const wouldOverflow = wouldOverflowList[index]
          return (
            <Item color={line?.color ?? 'black'} key={index} line={line} ref={getRef(index)} wouldOverflow={wouldOverflow}
                  enumerate={enumerate}/>
          )
        })}
        {errorMessages?.map((line, index) => {
          const text = line?.text
          if (text == null || text.length === 0) return null
          const wouldOverflow = wouldOverflowList[index]
          return (
            <Item color={ARGOS_ERROR} key={index} line={line} ref={getRef(index)} wouldOverflow={wouldOverflow}
                  enumerate={enumerate}/>
          )
        })}
        {warningMessages?.map((line, index) => {
            const text = line?.text
            if (text == null || text.length === 0) return null
            const wouldOverflow = wouldOverflowList[index]
            return (
              <Item color={ARGOS_WARNING} key={index} line={line} ref={getRef(index)} wouldOverflow={wouldOverflow}
                    enumerate={enumerate}/>
            )
          })}
      </div>
    </div>
  )
});
